import jsPDF from 'jspdf';
import type {ProtocolPdfDetails} from '../../../../../api/types/aiReview.type';
import {CASE_CONTEXT_TOC_SECTIONS} from '../../common/casecontextv2/caseContextConfig';

export const EMPTY_PROTOCOL_VALUE = 'not defined';

type PdfLineStyle =
  | 'title'
  | 'metadata'
  | 'section'
  | 'heading'
  | 'plainHeading'
  | 'footer'
  | 'separator'
  | 'field'
  | 'spacer';

export interface ProtocolPdfLine {
  text: string;
  level: number;
  style: PdfLineStyle;
  label?: string;
  value?: string;
}

export type ProtocolPdfContent = {
  caseContext?: Partial<ProtocolPdfDetails['caseContext']>;
  relevanceCriteria?: Partial<ProtocolPdfDetails['relevanceCriteria']>;
  issueCoding?: Partial<ProtocolPdfDetails['issueCoding']>;
};

type UnknownRecord = Record<string, unknown>;

const appendSpacer = (lines: ProtocolPdfLine[]) => {
  lines.push({
    text: '',
    level: 0,
    style: 'spacer',
  });
};

const PDF_STYLE_CONFIG = {
  title: {
    fontSize: 15,
    lineHeight: 4,
    fontStyle: 'bold' as const,
  },

  section: {
    fontSize: 12,
    lineHeight: 4,
    fontStyle: 'bold' as const,
  },

  heading: {
    fontSize: 10,
    lineHeight: 4,
    fontStyle: 'bold' as const,
  },
  plainHeading: {
    fontSize: 10,
    lineHeight: 4,
    fontStyle: 'bold' as const,
  },

  footer: {
    fontSize: 9,
    lineHeight: 4,
    fontStyle: 'normal' as const,
  },

  fieldLabel: {
    fontSize: 10,
    fontStyle: 'bold' as const,
  },

  fieldValue: {
    fontSize: 10,
    fontStyle: 'normal' as const,
  },

  metadata: {
    fontSize: 10,
    fontStyle: 'normal' as const,
    lineHeight: 4,
  },

  separator: {
    fontSize: 10,
    fontStyle: 'normal' as const,
  },

  field: {
    lineHeight: 4,
  },

  spacer: {
    height: 2,
  },
};

const appendPlainHeading = (lines: ProtocolPdfLine[], heading: string) => {
  appendLine(lines, heading, 0, 'plainHeading');
};

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasOwn = (value: UnknownRecord, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);

const hasValidOptionalString = (value: UnknownRecord, key: string) =>
  !hasOwn(value, key) || value[key] === null || typeof value[key] === 'string';

const hasValidOptionalStringOrNumber = (value: UnknownRecord, key: string) =>
  !hasOwn(value, key) ||
  value[key] === null ||
  typeof value[key] === 'string' ||
  typeof value[key] === 'number';

export const isProtocolVersionDetails = (
  value: unknown
): value is ProtocolPdfDetails => {
  if (!isRecord(value)) return false;

  if (typeof value.protocolName !== 'string' && value.protocolName !== null) {
    return false;
  }

  if (!hasValidOptionalString(value, 'latestVersion')) return false;

  for (const sectionKey of [
    'caseContext',
    'relevanceCriteria',
    'issueCoding',
  ]) {
    if (!hasOwn(value, sectionKey)) return false;
    const section = value[sectionKey];
    if (section !== null && !isRecord(section)) return false;
  }

  if (
    hasOwn(value, 'fileDetails') &&
    value.fileDetails !== null &&
    !isRecord(value.fileDetails)
  ) {
    return false;
  }

  for (const metadataKey of ['lastModifiedAt', 'lastModifiedBy', 'status']) {
    if (!hasValidOptionalString(value, metadataKey)) return false;
  }

  if (isRecord(value.fileDetails)) {
    if (!hasValidOptionalString(value.fileDetails, 'fileName')) {
      return false;
    }
    if (
      !hasValidOptionalStringOrNumber(
        value.fileDetails,
        'reviewProtocolDocumentID'
      )
    ) {
      return false;
    }
    for (const metadataKey of ['lastModifiedAt', 'lastModifiedBy']) {
      if (!hasValidOptionalString(value.fileDetails, metadataKey)) {
        return false;
      }
    }
  }

  return true;
};

export const normalizeProtocolValue = (value: unknown): string => {
  if (value === null || value === undefined) return EMPTY_PROTOCOL_VALUE;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || EMPTY_PROTOCOL_VALUE;
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }

  return EMPTY_PROTOCOL_VALUE;
};

const humanizeKey = (key: string): string =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const FIELD_LABELS: Record<string, string> = {
  protocolName: 'Protocol Name',
  latestVersion: 'Latest Version',
  status: 'Status',
  fileName: 'File Name',
  reviewProtocolDocumentID: 'Review Protocol Document ID',
  lastModifiedAt: 'Last Modified Date',
  lastModifiedBy: 'Last Modified By',
  executive_summary: 'Executive Summary',
  parties_and_roles: 'Parties and Roles',
  party: 'Party Name',
  role: 'Role',
  party_description: 'Relationship to Case',
  claims: 'Claims',
  defenses: 'Defenses',
  disputed_facts: 'Disputed Facts',
  key_legal_and_factual_issues: 'Key Legal and Factual Issues',
  primary_date_range: 'Primary Date Range',
  important_subperiods: 'Important Subperiods',
  custodians: 'Custodians',
  departments: 'Departments',
  systems_and_repositories: 'Systems & Repositories',
  responsiveness_standard_definition: 'Definition',
  responsive_examples: 'Responsive Examples',
  non_responsive_examples: 'Non-Responsive Examples',
  relevancy_keywords: 'Relevance Keywords',
  relevance_themes: 'Relevance Themes',
  behavioral_indicators: 'Behavioral Indicators',
  decision_making_indicators: 'Decision-Making Indicators',
  explicit_exclusions: 'Explicit Exclusions',
  reviewer_instructions: 'Reviewer Instructions',
  borderline_scenarios: 'Borderline Scenarios',
  ambiguities_and_known_limits: 'Ambiguities & Known Limits',
  master_relevance_standard: 'Master Relevance Criteria',
  exclusion_gates: 'Exclusion Gates (Hard Rules)',
  primary_decision_questions: 'Primary Decision Questions',
  secondary_supporting_questions: 'Secondary Supporting Questions',
  negative_exclusion_questions: 'Negative / Exclusion Questions',
  escalation_triggers: 'Escalating Triggers',
  id: 'ID',
  other: 'Other',
  hard_rule: 'Hard Rule',
  tag: 'Tag',
  question_text: 'Question Text',
  rationale: 'Rationale',
  purpose: 'Purpose',
  category_id: 'ID',
  category_label: 'Category Name',
  category_description: 'Definition',
  top_level_issue_categories: 'Top-Level Issue Categories',
  issue_codes: 'Issue Codes',
  code_id: 'Code ID',
  code_name: 'Code Name',
  category: 'Category',
  priority: 'Priority (Core, Important, Peripheral)',
  definition: 'Definition',
  include_if_any_apply: 'Include If Any Apply',
  exclude_if_any_apply: 'Exclude If Any Apply',
  cross_issue_overlap_rules: 'Cross-Issue Overlap Rules',
  edge_case_handling: 'Edge Case Handling',
};

const CASE_CONTEXT_SECTION_LABELS: Record<string, string> = {
  'case-overview': 'Case Overview',
  'review-scope': 'Review Scope',
  'ai-guidance': 'AI Guidance',
  'review-guidance': 'Review Guidance',
};

const getCaseContextFieldKeys = (sectionId: string): string[] => {
  const section = CASE_CONTEXT_TOC_SECTIONS.find(
    (candidate) => candidate.id === sectionId
  );
  const fieldKeys = section?.fields.map((field) => String(field.key)) ?? [];

  if (sectionId === 'case-overview') {
    fieldKeys.push('important_subperiods');
  }

  return fieldKeys;
};

const RELEVANT_TIME_PERIOD_FIELD_KEYS = [
  'primary_date_range',
  'important_subperiods',
] as const;

const RESPONSIVE_STANDARDS_FIELD_KEYS = [
  'responsiveness_standard_definition',
  'responsive_examples',
  'non_responsive_examples',
] as const;

const RELEVANCE_FIELD_KEYS = [
  'master_relevance_standard',
  'exclusion_gates',
  'primary_decision_questions',
  'secondary_supporting_questions',
  'negative_exclusion_questions',
  'escalation_triggers',
] as const;

const ISSUE_CODING_FIELD_KEYS = [
  'top_level_issue_categories',
  'issue_codes',
  'cross_issue_overlap_rules',
  'edge_case_handling',
] as const;

const FILE_DETAILS_FIELD_KEYS = [
  'fileName',
  'lastModifiedAt',
  'lastModifiedBy',
] as const;

const PDF_INDENT_SIZE = 6;
const PDF_FIELD_VALUE_GAP = 3;
const PDF_MIN_INLINE_VALUE_WIDTH = 20;

const NESTED_FIELD_KEYS: Record<string, readonly string[]> = {
  parties_and_roles: ['party', 'role', 'party_description'],
  exclusion_gates: ['id', 'other', 'hard_rule', 'tag'],
  primary_decision_questions: ['id', 'question_text', 'rationale'],
  secondary_supporting_questions: ['id', 'question_text', 'purpose'],
  negative_exclusion_questions: ['id', 'question_text', 'purpose'],
  escalation_triggers: ['id', 'question_text', 'purpose'],
  top_level_issue_categories: [
    'category_id',
    'category_label',
    'category_description',
  ],
  issue_codes: [
    'code_id',
    'code_name',
    'category',
    'priority',
    'definition',
    'include_if_any_apply',
    'exclude_if_any_apply',
    'responsive_examples',
    'non_responsive_examples',
  ],
};

const appendLine = (
  lines: ProtocolPdfLine[],
  text: string,
  level: number,
  style: PdfLineStyle = 'field',
  metadata: Pick<ProtocolPdfLine, 'label' | 'value'> = {}
) => {
  lines.push({text, level, style, ...metadata});
};

const appendField = (
  lines: ProtocolPdfLine[],
  label: string,
  value: unknown,
  level: number,
  style: PdfLineStyle = 'field'
) => {
  const normalizedValue = normalizeProtocolValue(value);
  appendLine(lines, `${label}: ${normalizedValue}`, level, style, {
    label,
    value: normalizedValue,
  });
};

const isBlankStructuredValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) {
    return value.length === 0 || value.every(isBlankStructuredValue);
  }
  if (isRecord(value)) {
    const entries = Object.values(value);
    return entries.length === 0 || entries.every(isBlankStructuredValue);
  }
  return false;
};

const appendValueLines = (
  lines: ProtocolPdfLine[],
  label: string,
  value: unknown,
  level: number,
  fieldKey?: string,
  expectedFieldKeys?: readonly string[]
) => {
  const labelLevel = level;

  // Primitive Values
  if (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    appendField(lines, label, value, labelLevel);
    return;
  }

  // Arrays
  if (Array.isArray(value)) {
    if (value.length === 0) {
      appendField(lines, label, EMPTY_PROTOCOL_VALUE, labelLevel);
      return;
    }

    appendLine(lines, `${label}:`, labelLevel, 'heading', {label});

    value.forEach((item, index) => {
      const isLastItem = index === value.length - 1;

      // Primitive Array
      if (!isRecord(item)) {
        appendLine(
          lines,
          normalizeProtocolValue(item),
          labelLevel + 1,
          'field'
        );
      } else {
        // Object Array
        const nestedFieldKeys =
          expectedFieldKeys ??
          NESTED_FIELD_KEYS[fieldKey ?? ''] ??
          Object.keys(item);

        const knownKeys = new Set(nestedFieldKeys);

        nestedFieldKeys.forEach((key) => {
          appendValueLines(
            lines,
            FIELD_LABELS[key] ?? humanizeKey(key),
            item[key],
            labelLevel + 1,
            key
          );
        });

        Object.entries(item)
          .filter(([key]) => !knownKeys.has(key))
          .forEach(([key, nestedValue]) => {
            appendValueLines(
              lines,
              FIELD_LABELS[key] ?? humanizeKey(key),
              nestedValue,
              labelLevel + 1,
              key
            );
          });
      }

      if (!isLastItem) {
        appendLine(lines, '---', labelLevel + 1, 'separator');
      }
    });

    return;
  }

  // Objects
  if (isRecord(value)) {
    const nestedFieldKeys =
      expectedFieldKeys ?? NESTED_FIELD_KEYS[fieldKey ?? ''];

    if (!nestedFieldKeys && isBlankStructuredValue(value)) {
      appendField(lines, label, EMPTY_PROTOCOL_VALUE, labelLevel);
      return;
    }

    appendLine(lines, `${label}:`, labelLevel, 'heading');

    const knownKeys = new Set(nestedFieldKeys ?? []);

    const keysToRender = nestedFieldKeys ?? Object.keys(value);

    keysToRender.forEach((key) => {
      appendValueLines(
        lines,
        FIELD_LABELS[key] ?? humanizeKey(key),
        value[key],
        labelLevel + 1,
        key
      );
    });

    Object.entries(value)
      .filter(([key]) => !knownKeys.has(key))
      .forEach(([key, nestedValue]) => {
        appendValueLines(
          lines,
          FIELD_LABELS[key] ?? humanizeKey(key),
          nestedValue,
          labelLevel + 1,
          key
        );
      });

    return;
  }

  appendField(lines, label, EMPTY_PROTOCOL_VALUE, labelLevel);
};

const appendSection = (
  lines: ProtocolPdfLine[],
  heading: string,
  value: unknown,
  fieldKeys: readonly string[],
  includeExtraFields = true
) => {
  appendLine(lines, heading, 0, 'section');
  const section = isRecord(value) ? value : {};
  const knownKeys = new Set(fieldKeys);

  fieldKeys.forEach((key) => {
    appendValueLines(
      lines,
      FIELD_LABELS[key] ?? humanizeKey(key),
      section[key],
      0,
      key
    );
  });

  if (includeExtraFields) {
    Object.entries(section)
      .filter(([key]) => !knownKeys.has(key))
      .forEach(([key, nestedValue]) => {
        appendValueLines(
          lines,
          FIELD_LABELS[key] ?? humanizeKey(key),
          nestedValue,
          0,
          key
        );
      });
  }

  appendSpacer(lines);
};

const getLastModifiedValue = (
  protocol: ProtocolPdfDetails,
  key: 'lastModifiedAt' | 'lastModifiedBy'
) =>
  protocol[key] ??
  (isRecord(protocol.fileDetails) ? protocol.fileDetails[key] : undefined);

const getProtocolStatus = (status: unknown): string => {
  const normalizedStatus = normalizeProtocolValue(status).toLowerCase();
  return normalizedStatus === 'approved' ? 'Approved' : 'Draft';
};

export const serializeProtocolPdfContent = (
  protocol: ProtocolPdfContent,
  sections: {
    includeRelevanceCriteria?: boolean;
    includeIssueCoding?: boolean;
  } = {}
): ProtocolPdfLine[] => {
  const includeRelevanceCriteria = sections.includeRelevanceCriteria !== false;
  const includeIssueCoding = sections.includeIssueCoding !== false;
  const lines: ProtocolPdfLine[] = [];
  const caseContext = isRecord(protocol.caseContext)
    ? protocol.caseContext
    : {};

  CASE_CONTEXT_TOC_SECTIONS.forEach((section) => {
    const fieldKeys = getCaseContextFieldKeys(section.id);
    const caseContextSectionHeading = `Case Context: ${
      CASE_CONTEXT_SECTION_LABELS[section.id] ?? humanizeKey(section.id)
    }`;

    if (section.id === 'case-overview') {
      appendSection(
        lines,
        caseContextSectionHeading,
        caseContext,
        fieldKeys.filter(
          (key) =>
            !RELEVANT_TIME_PERIOD_FIELD_KEYS.includes(
              key as (typeof RELEVANT_TIME_PERIOD_FIELD_KEYS)[number]
            )
        ),
        false
      );
      appendLine(lines, 'Relevant Time Periods', 0, 'heading');
      RELEVANT_TIME_PERIOD_FIELD_KEYS.forEach((key) => {
        appendValueLines(lines, FIELD_LABELS[key], caseContext[key], 1, key);
      });
      appendSpacer(lines);
      return;
    }

    if (section.id === 'review-scope') {
      appendSection(
        lines,
        caseContextSectionHeading,
        caseContext,
        fieldKeys.filter(
          (key) =>
            !RESPONSIVE_STANDARDS_FIELD_KEYS.includes(
              key as (typeof RESPONSIVE_STANDARDS_FIELD_KEYS)[number]
            )
        ),
        false
      );
      appendLine(lines, 'Responsive Standards', 0, 'heading');
      RESPONSIVE_STANDARDS_FIELD_KEYS.forEach((key) => {
        appendValueLines(lines, FIELD_LABELS[key], caseContext[key], 1, key);
      });
      appendSpacer(lines);
      return;
    }

    appendSection(
      lines,
      caseContextSectionHeading,
      caseContext,
      fieldKeys,
      false
    );
  });

  const caseContextKnownKeys = new Set<string>(
    CASE_CONTEXT_TOC_SECTIONS.flatMap((section) =>
      getCaseContextFieldKeys(section.id)
    )
  );

  const additionalCaseContextFields = Object.fromEntries(
    Object.entries(caseContext).filter(
      ([key]) => !caseContextKnownKeys.has(key)
    )
  );

  if (Object.keys(additionalCaseContextFields).length > 0) {
    appendSection(
      lines,
      'Case Context: Additional Fields',
      additionalCaseContextFields,
      []
    );
  }

  if (includeRelevanceCriteria) {
    appendSection(
      lines,
      'Relevance Criteria',
      protocol.relevanceCriteria,
      RELEVANCE_FIELD_KEYS
    );
  }

  if (includeIssueCoding) {
    appendSection(
      lines,
      'Issue Coding',
      protocol.issueCoding,
      ISSUE_CODING_FIELD_KEYS
    );
  }

  return lines;
};

export const serializeProtocolPdf = (
  protocol: ProtocolPdfDetails
): ProtocolPdfLine[] => {
  const lines: ProtocolPdfLine[] = [];

  appendLine(
    lines,
    `Protocol: ${normalizeProtocolValue(
      protocol.protocolName
    )} ${normalizeProtocolValue(protocol.latestVersion)}`,
    0,
    'title'
  );

  appendField(
    lines,
    'Last Modified Date',
    getLastModifiedValue(protocol, 'lastModifiedAt'),
    0,
    'metadata'
  );

  appendField(
    lines,
    'Last Modified By',
    getLastModifiedValue(protocol, 'lastModifiedBy'),
    0,
    'metadata'
  );

  appendField(
    lines,
    'Status',
    getProtocolStatus(protocol.status),
    0,
    'metadata'
  );

  appendSpacer(lines);

  lines.push(...serializeProtocolPdfContent(protocol));

  appendPlainHeading(lines, 'File Details');

  const fileDetails: UnknownRecord = isRecord(protocol.fileDetails)
    ? protocol.fileDetails
    : {};

  FILE_DETAILS_FIELD_KEYS.forEach((key) => {
    const value =
      key === 'lastModifiedAt' || key === 'lastModifiedBy'
        ? getLastModifiedValue(protocol, key)
        : fileDetails[key];

    appendLine(
      lines,
      `${FIELD_LABELS[key] ?? humanizeKey(key)}: ${normalizeProtocolValue(
        value
      )}`,
      0,
      'field'
    );
  });

  appendSpacer(lines);

  return lines;
};

export const sanitizeProtocolFilename = (protocolName: unknown): string => {
  const normalizedName = normalizeProtocolValue(protocolName);
  const sanitizedControlCharacters = normalizedName
    .split('')
    .map((character) => (character.charCodeAt(0) < 32 ? '_' : character))
    .join('');
  const baseName = (
    normalizedName === EMPTY_PROTOCOL_VALUE
      ? 'Protocol'
      : sanitizedControlCharacters
  )
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, ' ')
    .replace(/[. ]+$/, '')
    .trim()
    .slice(0, 120);

  return `Protocol_${baseName || 'Protocol'}.pdf`;
};

const ensurePdfSpace = (
  doc: jsPDF,
  currentY: number,
  lineHeight: number,
  margin: number
): number => {
  if (currentY + lineHeight > doc.internal.pageSize.getHeight() - margin) {
    doc.addPage();
    return margin;
  }
  return currentY;
};

const drawWrappedText = (
  doc: jsPDF,
  text: string,
  x: number,
  currentY: number,
  width: number,
  lineHeight: number,
  margin: number
): number => {
  const wrappedLines = doc.splitTextToSize(text, Math.max(width, 20));
  const lines = wrappedLines.length > 0 ? wrappedLines : [''];
  let y = currentY;

  lines.forEach((wrappedLine: string) => {
    y = ensurePdfSpace(doc, y, lineHeight, margin);
    doc.text(wrappedLine, x, y);
    y += lineHeight;
  });

  return y;
};

const addPageFooters = (doc: jsPDF) => {
  const totalPages = doc.getNumberOfPages();

  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);

    doc.text(`Page ${page} / ${totalPages}`, pageWidth - 15, pageHeight - 10, {
      align: 'right',
    });
  }
};

const drawFieldLine = (
  doc: jsPDF,
  line: ProtocolPdfLine,
  x: number,
  width: number,
  currentY: number,
  lineHeight: number,
  margin: number
): number => {
  let y = currentY;

  if (!line.label && line.value === undefined) {
    doc.setFont('helvetica', PDF_STYLE_CONFIG.fieldValue.fontStyle);

    doc.setFontSize(PDF_STYLE_CONFIG.fieldValue.fontSize);

    return drawWrappedText(doc, line.text, x, y, width, lineHeight, margin);
  }
  const labelText = `${line.label}:`;

  doc.setFont('helvetica', PDF_STYLE_CONFIG.fieldLabel.fontStyle);

  doc.setFontSize(PDF_STYLE_CONFIG.fieldLabel.fontSize);

  const labelWidth = doc.getTextWidth(labelText);
  const renderValueInline =
    labelWidth + PDF_FIELD_VALUE_GAP + PDF_MIN_INLINE_VALUE_WIDTH <= width;
  const valueX = renderValueInline
    ? x + labelWidth + PDF_FIELD_VALUE_GAP
    : x + PDF_INDENT_SIZE;
  const valueWidth = renderValueInline
    ? Math.max(width - labelWidth - PDF_FIELD_VALUE_GAP, 20)
    : Math.max(width - PDF_INDENT_SIZE, 20);

  const value = line.value ?? EMPTY_PROTOCOL_VALUE;
  const wrappedValues: string[] = doc.splitTextToSize(
    value,
    valueWidth
  ) as string[];

  const values =
    wrappedValues.length > 0 ? wrappedValues : [EMPTY_PROTOCOL_VALUE];

  if (!renderValueInline) {
    y = ensurePdfSpace(doc, y, lineHeight, margin);
    doc.text(labelText, x, y);
    y += lineHeight;

    values.forEach((wrappedValue: string) => {
      y = ensurePdfSpace(doc, y, lineHeight, margin);
      doc.setFont('helvetica', PDF_STYLE_CONFIG.fieldValue.fontStyle);
      doc.setFontSize(PDF_STYLE_CONFIG.fieldValue.fontSize);
      doc.text(wrappedValue, valueX, y);
      y += lineHeight;
    });

    return y;
  }

  values.forEach((wrappedValue: string, index: number) => {
    y = ensurePdfSpace(doc, y, lineHeight, margin);

    if (index === 0) {
      doc.setFont('helvetica', PDF_STYLE_CONFIG.fieldLabel.fontStyle);

      doc.setFontSize(PDF_STYLE_CONFIG.fieldLabel.fontSize);

      doc.text(labelText, x, y);
    }

    doc.setFont('helvetica', PDF_STYLE_CONFIG.fieldValue.fontStyle);

    doc.setFontSize(PDF_STYLE_CONFIG.fieldValue.fontSize);

    doc.text(wrappedValue, valueX, y);

    y += lineHeight;
  });

  return y;
};

export const createProtocolPdf = (protocol: ProtocolPdfDetails): void => {
  const doc = new jsPDF();

  const margin = 15;

  const contentWidth = doc.internal.pageSize.getWidth() - margin * 2;

  const lines = serializeProtocolPdf(protocol);

  let y = margin;

  lines.forEach((line) => {
    const indent = line.level === 0 ? 0 : line.level * PDF_INDENT_SIZE;

    const currentX = margin + indent;

    const printableWidth = Math.max(contentWidth - indent, 20);

    if (line.style === 'spacer') {
      y += PDF_STYLE_CONFIG.spacer.height;
      return;
    }

    if (!line.text.trim()) {
      return;
    }

    if (line.style === 'title') {
      doc.setFont('helvetica', PDF_STYLE_CONFIG.title.fontStyle);

      doc.setFontSize(PDF_STYLE_CONFIG.title.fontSize);

      doc.setTextColor(26, 26, 26);

      y = drawWrappedText(
        doc,
        line.text,
        currentX,
        y,
        printableWidth,
        PDF_STYLE_CONFIG.title.lineHeight,
        margin
      );

      return;
    }

    if (line.style === 'section') {
      doc.setFont('helvetica', PDF_STYLE_CONFIG.section.fontStyle);

      doc.setFontSize(PDF_STYLE_CONFIG.section.fontSize);

      doc.setTextColor(26, 26, 26);

      y = drawWrappedText(
        doc,
        line.text,
        currentX,
        y,
        printableWidth,
        PDF_STYLE_CONFIG.section.lineHeight,
        margin
      );

      return;
    }

    if (line.style === 'heading') {
      doc.setFont('helvetica', PDF_STYLE_CONFIG.heading.fontStyle);

      doc.setFontSize(PDF_STYLE_CONFIG.heading.fontSize);

      doc.setTextColor(26, 26, 26);

      y = drawWrappedText(
        doc,
        line.text,
        currentX,
        y,
        printableWidth,
        PDF_STYLE_CONFIG.heading.lineHeight,
        margin
      );

      return;
    }

    if (line.style === 'plainHeading') {
      doc.setFont('helvetica', PDF_STYLE_CONFIG.plainHeading.fontStyle);

      doc.setFontSize(PDF_STYLE_CONFIG.plainHeading.fontSize);

      doc.setTextColor(26, 26, 26);

      y = drawWrappedText(
        doc,
        line.text,
        currentX,
        y,
        printableWidth,
        PDF_STYLE_CONFIG.plainHeading.lineHeight,
        margin
      );

      return;
    }
    if (line.style === 'separator') {
      doc.setFont('helvetica', PDF_STYLE_CONFIG.separator.fontStyle);

      doc.setFontSize(PDF_STYLE_CONFIG.separator.fontSize);

      doc.setTextColor(26, 26, 26);

      y = drawWrappedText(
        doc,
        line.text,
        currentX,
        y,
        printableWidth,
        PDF_STYLE_CONFIG.field.lineHeight,
        margin
      );

      return;
    }

    if (line.style === 'field' || line.style === 'metadata') {
      const fieldLineHeight =
        line.style === 'metadata'
          ? PDF_STYLE_CONFIG.metadata.lineHeight
          : PDF_STYLE_CONFIG.field.lineHeight;

      y = drawFieldLine(
        doc,
        line,
        currentX,
        printableWidth,
        y,
        fieldLineHeight,
        margin
      );

      return;
    }
  });

  addPageFooters(doc);

  doc.save(sanitizeProtocolFilename(protocol.protocolName));
};
